/**
 * @template T
 * @param {{ apiClient: import('../contracts/api.js').ApiClient, basePath: string }} config
 * @returns {import('../contracts/adapter.js').CrudAdapter<T>}
 */
export function createRestCrudAdapter({ apiClient, basePath }) {
  // --- basePath validation & normalization ---
  if (typeof basePath !== 'string') throw new Error('basePath must be a string');
  const raw = basePath.trim();
  if (!raw) throw new Error('basePath must be a non-empty resource path (e.g. "/regions")');
  if (raw.includes('?') || raw.includes('#')) {
    throw new Error('basePath must not contain query or fragment (? or #)');
  }
  if (raw.includes('\\')) {
    throw new Error('basePath must not contain backslashes');
  }
  if (/\s/.test(raw)) {
    throw new Error('basePath must not contain whitespace');
  }
  const normalizedPath = '/' + raw.replace(/^\/+/, '').replace(/\/+$/, '');
  if (normalizedPath === '/') throw new Error('basePath must be a non-empty resource path (e.g. "/regions")');

  // --- Reserved filter keys (belt-and-suspenders for direct adapter usage) ---
  const RESERVED = new Set(['page', 'pageSize', 'sortField', 'sortDir']);

  /**
   * @param {string | number} id
   * @returns {string}
   */
  function buildIdUrl(id) {
    if (id == null || String(id).trim() === '') throw new Error('id is required');
    const idStr = String(id).trim();
    return `${normalizedPath}/${encodeURIComponent(idStr)}`;
  }

  return {
    async fetchPage(query, signal) {
      /** @type {Record<string, string | string[]>} */
      const params = {};

      params.page = String(query.page);
      params.pageSize = String(query.pageSize);

      if (typeof query.sortField === 'string' && query.sortField.trim() !== '') {
        params.sortField = query.sortField;
      }
      if (typeof query.sortDir === 'string' && query.sortDir.trim() !== '') {
        params.sortDir = query.sortDir;
      }

      // Reserved-key check runs BEFORE filter omission — reserved keys throw even if value is ''.
      // A reserved key in filters signals a miswired driver, regardless of value.
      const filters = query.filters ?? {};
      for (const key of Object.keys(filters)) {
        if (RESERVED.has(key)) {
          throw new Error(`Filter key '${key}' is reserved (collides with pagination/sort params)`);
        }
      }

      for (const [key, value] of Object.entries(filters)) {
        // Omission rules
        if (value == null) continue;
        if (typeof value === 'string' && value.trim() === '') continue;

        if (Array.isArray(value)) {
          const clean = value
            .filter(v => v != null && String(v).trim() !== '')
            .map(v => String(v));
          if (clean.length === 0) continue;
          params[key] = clean;
        } else {
          params[key] = String(value);
        }
      }

      return /** @type {Promise<import('../contracts/grid.js').GridPage<T>>} */ (
        apiClient.get(normalizedPath, { params, signal })
      );
    },

    async get(id, signal) {
      return /** @type {Promise<T>} */ (apiClient.get(buildIdUrl(id), { signal }));
    },

    async create(data, signal) {
      return /** @type {Promise<T>} */ (apiClient.post(normalizedPath, data, { signal }));
    },

    async update(id, data, signal) {
      return /** @type {Promise<T>} */ (apiClient.put(buildIdUrl(id), data, { signal }));
    },

    async delete(id, signal) {
      await apiClient.delete(buildIdUrl(id), undefined, { signal });
    },

    async reorder(id, toIndex, signal) {
      await apiClient.patch(buildIdUrl(id) + '/reorder', { toIndex }, { signal });
    },
  };
}
